import { FC, ReactElement } from 'react';
import { captureException } from '@sentry/react';
import { DropdownProps } from 'antd';
import { TbFlag } from 'react-icons/tb';

import { taskService } from '@/services';
import DropdownButtonSelect, {
    DropdownButtonOption,
} from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { Task, TaskPriority } from '@/types';

import TaskPriorityIcon from '../../tasks/TaskPriorityIcon';

interface PriorityDropdownProps {
    children: ReactElement;
    disabled?: boolean;
    onOpenChange?: (open: boolean) => void;
    onTaskUpdated?: (task: Task) => void;
    placement?: DropdownProps['placement'];
    task?: Task;
    priority: TaskPriority | '' | undefined;
    setPriority: (priority: TaskPriority | '') => void;
}

const PriorityDropdown: FC<PriorityDropdownProps> = ({
    children,
    disabled,
    onOpenChange,
    onTaskUpdated,
    placement,
    task,
    priority,
    setPriority,
}): JSX.Element => {
    const handleChangePriority = async (key: string) => {
        const nextPriority = key === '' ? '' : Object.values(TaskPriority).find((value) => value === key);

        if (nextPriority === undefined) {
            return;
        }

        if (task?.id) {
            const previousPriority = priority ?? '';

            setPriority(nextPriority);
            try {
                const updatedTask = await taskService.setPriority(task.id, nextPriority);

                setPriority(updatedTask.priority ?? '');
                onTaskUpdated?.(updatedTask);
            } catch (error) {
                setPriority(previousPriority);
                captureException(error);
            }
        } else {
            setPriority(nextPriority);
        }
    };

    const options: Array<DropdownButtonOption<TaskPriority | ''>> = [
        {
            icon: <TaskPriorityIcon priority={TaskPriority.URGENT} />,
            label: TaskPriority.URGENT,
            value: TaskPriority.URGENT,
        },
        {
            icon: <TaskPriorityIcon priority={TaskPriority.HIGH} />,
            label: TaskPriority.HIGH,
            value: TaskPriority.HIGH,
        },
        {
            icon: <TaskPriorityIcon priority={TaskPriority.MEDIUM} />,
            label: TaskPriority.MEDIUM,
            value: TaskPriority.MEDIUM,
        },
        {
            icon: <TaskPriorityIcon priority={TaskPriority.LOW} />,
            label: TaskPriority.LOW,
            value: TaskPriority.LOW,
        },
        {
            dividerBefore: true,
            icon: <TaskPriorityIcon priority='' />,
            label: 'No Priority',
            value: '',
        },
    ];

    return (
        <DropdownButtonSelect
            footer={<LearnMoreFooter topic='task priorities and planning' variant='dropdown' />}
            header={<DropdownMenuLabel icon={<TbFlag size='0.875rem' />}>Priority</DropdownMenuLabel>}
            disabled={disabled}
            matchTriggerWidth={false}
            menuWidth='13rem'
            onOpenChange={onOpenChange}
            options={options}
            placement={placement}
            value={priority ?? ''}
            onChange={(nextPriority) => void handleChangePriority(nextPriority ?? '')}
        >
            {children}
        </DropdownButtonSelect>
    );
};

export default PriorityDropdown;
